import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { GridRowModes, useGridApiRef } from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns, generateAutocompleteItems } from '../../utils/helper';
import { DataGridIdentifiers, DatagridPremiumUi, InputUI } from '../Interface';
import prodgeneratorService from '../../service/prodgenerator.service';
import useUsers from '../../context/UsersContext';
import useConfig from '../../context/ConfigContext';
import masterDataService from '../../service/masterData.service';
import DrillArrow from '../Interface/DrillArrow';
import SourceListDialog from '../SourceList/SourceListDialog';
import { TechnologistStatuses } from '../Client/VaradiMetal/ProductionPlannerTable/TechnologistStatuses';
import { useLoader } from '../../provider/LoaderProvider';
import { productionGeneratorGridSx } from './ProductionGenerator.style';

const ProductionGeneratorTable = ({
  selectedRowsState,
  selectedItemsState,
  removedItemState,
  sourceDataState,
  refetchState,
  treeDataState,
  expandedItemsState,
}) => {
  const apiRef = useGridApiRef();
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();
  const { showLoader, hideLoader } = useLoader();

  const { selectedRows, setSelectedRows } = selectedRowsState;
  const { setSelectedItems } = selectedItemsState;
  const { setRemovedItem } = removedItemState;
  const { refetchTable, setRefetchTable } = refetchState;
  const { setTreeData } = treeDataState;
  const { setExpandedItems } = expandedItemsState;
  const { setSourceData } = sourceDataState;

  const [autocompleteData, setAutocompleteData] = useState({});
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({});
  const [selectedItemCode, setSelectedItemCode] = useState(null);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
    initDefaultValues();
  }, []);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          users: resp[0],
        };
        setAutocompleteData(aData);

        const technologists = aData.users.filter((user) => user.technologist);

        const acOptions = {
          technologists: generateAutocompleteItems(technologists, 'nickName', 'userId'),
        };
        setAutoCompleteOptions(acOptions);
        //setLoading(false);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const [reports, setReports] = useState({ rows: [], rowCount: 0 });

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'dueDate', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 100,
        page: 0,
      },
    },
    density: 'compact',
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (Object.keys(user).length) {
      setLoading(false);
    }
  }, [user]);

  const getList = (gridState) => {
    prodgeneratorService.getOrders(gridState).then((res) => {
      setReports(res);
    });
  };

  const handleFieldChange = async (fieldName, newValue, row) => {
    try {
      const value = newValue === '' ? null : newValue;
      const requestBody = {
        ...row,
        [fieldName]: value,
      };

      await prodgeneratorService.updateOrderLine(requestBody).then(() => {
        setReports((prev) => ({
          ...prev,
          rows: prev.rows.map((r) => (r.generatedNum === row.generatedNum ? { ...r, [fieldName]: value } : r)),
        }));
      });
    } catch (error) {
      console.error('Error updating field:', error);
    }
  };

  const columns = (autoCompleteOptions, sourceListDialogOpen) => {
    return dataGridPremiumColumns([
      /*{
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        renderHeader: () => <></>,
      },*/
      {
        field: 'dueDate',
        headerName: 'Határidő',
        type: 'date',
        valueGetter: (value) => new Date(value),
        valueFormatter: (value) => dayjs(value).format('YYYY.MM.DD.'),
      },
      {
        field: 'docNum',
        headerName: 'Bizonylatszám',
        type: 'string',
      },
      {
        field: 'lineNum',
        headerName: 'Sor',
        type: 'string',
      },
      {
        field: 'itemCode',
        headerName: 'Cikk',
        type: 'string',
        renderCell: (params) => {
          return (
            <div>
              {params.row.itemCode}

              <DrillArrow
                onClick={(e) => {
                  sourceListDialogOpen(params.row.itemCode);
                }}
              />
            </div>
          );
        },
      },
      {
        field: 'itemName',
        headerName: 'Cikk neve',
        type: 'string',
      },
      {
        field: 'qty',
        headerName: 'Rendelt menny.',
        type: 'number',
      },
      {
        field: 'doc_joker_num2',
        headerName: 'Gyártandó menny.',
        type: 'number',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="doc_joker_num2"
              type="number"
              variant="standard"
              fullWidth
              value={data.row.doc_joker_num2}
              onChange={(e) => {
                const newValue = e.target.value ?? null;
                handleFieldChange(data.field, newValue, data.row);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });
              }}
            />
          );
        },
      },
      {
        field: 'quantityUnitCode',
        headerName: 'Me.kód',
        type: 'string',
      },
      {
        field: 'reference',
        headerName: 'Rend hiv. szám',
        type: 'string',
      },
      {
        field: 'itemReference',
        headerName: 'Tétel hiv. szám',
        type: 'string',
      },
      {
        field: 'companyCode',
        headerName: 'Partner kód',
        type: 'string',
      },
      {
        field: 'companyName',
        headerName: 'Partner név',
        type: 'string',
      },
      {
        field: 'onHand',
        headerName: 'Készleten',
        type: 'number',
      },
      {
        field: 'missingTree',
        headerName: 'Hiányos darabjegyzék',
        type: 'boolean',
      },
      {
        field: 'missingProdOrder',
        headerName: 'Hiányos gyártás',
        type: 'boolean',
      },
      {
        field: 'hasProduction',
        headerName: 'Készült hozzá gyártás',
        type: 'boolean',
      },

      {
        field: 'doc_joker_int1',
        headerName: 'Technológus',
        type: 'singleSelect',
        sortable: false,
        valueOptions: autoCompleteOptions.technologists,
      },
      {
        field: 'doc_joker_char7',
        headerName: 'Technológus státusz',
        type: 'singleSelect',
        valueOptions: TechnologistStatuses,
      },
      {
        field: 'doc_joker_char3',
        headerName: 'Gyártás helye',
        type: 'string',
      },
    ]);
  };

  const getRowClassName = (params) => {
    let classNames = '';

    if (params.row.missingSourceList) classNames += 'datagrid-line-missingSourceList datagrid-line-hover ';
    else if (params.row.missingProdOrder) classNames += 'datagrid-line-missingProdOrder datagrid-line-hover ';
    else if (params.row.doc_joker_char7 !== 'FULLDOC') classNames += 'datagrid-line-bg_yellow datagrid-line-hover ';

    if (params.row.missingTree || params.row.missingOperation) classNames += 'datagrid-line-missingTree ';
    if (params.row.hasProduction && !params.row.missingTree) classNames += 'datagrid-line-hasProduction ';

    return classNames.trim();
  };

  const sourceListDialogOpen = (itemCode) => {
    setSelectedItemCode(itemCode);
  };

  const sourceListDialogClose = () => {
    setSelectedItemCode(null);
  };

  const handleSelectionModelChange = async (newRowSelectionModel) => {
    const added = newRowSelectionModel.filter((id) => !selectedRows.includes(id));
    const removed = selectedRows.filter((id) => !newRowSelectionModel.includes(id));

    const items = [];
    for (const id of added) {
      const findedRow = reports.rows.find((row) => row.generatedNum === id);
      if (findedRow) {
        items.push(findedRow);
        setRemovedItem(null);
      }
    }
    setSelectedItems(items);

    for (const id of removed) {
      showLoader();
      const removedRow = reports.rows.find((row) => row.generatedNum === id);
      if (removedRow) {
        setRemovedItem(removedRow);
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }

    hideLoader();

    setSelectedRows(newRowSelectionModel);
  };

  const getTreeData = (itemCode) => {
    showLoader();
    prodgeneratorService
      .getsourcelistByItemCode({ itemCode })
      .then((data) => {
        setTreeData(data.treeView);
        setSourceData(data.sourceData);
        setExpandedItems(getAllItemIds(data.treeView));
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getAllItemIds = (items, ids = []) => {
    if (!items.length) return ids;
    items.forEach((item) => {
      ids.push(item.id);
      if (item.children) getAllItemIds(item.children, ids);
    });
    return ids;
  };

  return (
    <>
      {!loading && (
        <DatagridPremiumUi
          apiRef={apiRef}
          datagridIdentifier={DataGridIdentifiers.ProductionGeneratorTable}
          sx={productionGeneratorGridSx}
          datagridInitialState={initialState}
          dataLoader={getList}
          height={400}
          rows={reports.rows}
          refetch={{ refetchTable, setRefetchTable }}
          rowCount={reports.rowCount}
          columns={columns(autoCompleteOptions, sourceListDialogOpen)}
          paginationAndfilterMode="server"
          onRowClick={(params) => {
            getTreeData(params.row.itemCode);
          }}
          getRowId={(row) => row.generatedNum}
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => handleSelectionModelChange(newRowSelectionModel)}
          rowSelectionModel={selectedRows}
          getRowClassName={getRowClassName}
          isRowSelectable={(params) => !params.row.hasProduction && params.row.doc_joker_char7 === 'FULLDOC'}
          disableRowSelectionOnClick
          keepNonExistentRowsSelected
          editMode="row"
          onCellDoubleClick={(_params, event) => {
            event.defaultMuiPrevented = true;
          }}
          onCellClick={(params, event) => {
            const { row } = params;

            if (!params.colDef?.editable) {
              return;
            }

            const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

            for (const rowId of rowsInEditMode) {
              if (row.generatedNum !== rowId) {
                apiRef.current.stopRowEditMode({ id: rowId });
              }
            }
            const rowMode = apiRef.current.getRowMode(row.generatedNum);
            if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: row.generatedNum });
          }}
        />
      )}
      <SourceListDialog
        showDialog={selectedItemCode}
        onClose={sourceListDialogClose}
        selectedItemCode={selectedItemCode}
      />
    </>
  );
};

export default ProductionGeneratorTable;
